/**
 * HelpPanel - In-app help and documentation
 * Organized by feature area with searchable content
 * Includes video tutorials
 */

import React, { useState } from 'react';
import { resetOnboarding } from '../onboarding/OnboardingFlow';

// Video tutorials - replace placeholder IDs with your actual YouTube video IDs
const VIDEO_TUTORIALS = [
  {
    id: 'overview',
    title: 'Getting Started Overview',
    description: 'A quick tour of FloorPlan Pro and its main features',
    // Replace with actual YouTube video ID (the part after v= in the URL)
    youtubeId: 'PLACEHOLDER_OVERVIEW',
    duration: '3:00',
  },
  {
    id: 'drawing',
    title: 'Drawing Walls & Rooms',
    description: 'Learn how to draw walls, create rooms, and add doors and windows',
    youtubeId: 'PLACEHOLDER_DRAWING',
    duration: '5:00',
  },
  {
    id: 'hvac-basics',
    title: 'HVAC Mode Basics',
    description: 'Introduction to HVAC mode - drawing ducts and placing vents',
    youtubeId: 'PLACEHOLDER_HVAC',
    duration: '4:00',
  },
  {
    id: 'load-calc',
    title: 'Load Calculations',
    description: 'How to use Manual J load calculations and manual entry mode',
    youtubeId: 'PLACEHOLDER_LOADCALC',
    duration: '6:00',
  },
  {
    id: 'exporting',
    title: 'Exporting & Sharing',
    description: 'Export your floor plan to PDF, image, or share with others',
    youtubeId: 'PLACEHOLDER_EXPORT',
    duration: '3:00',
  },
  {
    id: 'pdf-import',
    title: 'Importing & Tracing PDFs',
    description: 'Import existing floor plans and trace over them',
    youtubeId: 'PLACEHOLDER_PDF',
    duration: '4:00',
  },
];

const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    topics: [
      {
        title: 'Creating a New Project',
        content: 'Start with a blank canvas or import a PDF. Use File > New to start fresh, or File > Import PDF to trace over an existing plan.',
      },
      {
        title: 'Navigation',
        content: 'Pinch to zoom in/out on mobile, or use scroll wheel on desktop. Drag with two fingers (mobile) or click and drag (desktop) to pan around the canvas.',
      },
      {
        title: 'Saving Your Work',
        content: 'Use File > Save to save your project. Projects are stored in your browser. You can also export to PDF or image formats.',
      },
      {
        title: 'Units & Scale',
        content: 'The app uses feet and inches by default. When exporting, you can set the scale (e.g., 1/4" = 1\').',
      },
    ],
  },
  {
    id: 'drawing-tools',
    title: 'Drawing Tools',
    icon: '✏️',
    topics: [
      {
        title: 'Wall Tool',
        content: 'Select the Wall tool, then tap/click to place the start point. Tap again to place the end point. Walls snap to common angles (0°, 45°, 90°). Double-tap to finish a wall chain.',
      },
      {
        title: 'Room Tool',
        content: 'Draw enclosed rooms by tapping corners. The room will close automatically when you tap near the starting point or press Done. Rooms calculate area automatically.',
      },
      {
        title: 'Door & Window',
        content: 'Select Door or Window tool, then tap on an existing wall to place it. Drag to adjust position along the wall. Edit properties by selecting the placed item.',
      },
      {
        title: 'Polyline Tool',
        content: 'Draw multi-segment lines. Tap to add points, double-tap or press Done to finish. Great for counters, stairs, or custom shapes.',
      },
      {
        title: 'Text Tool',
        content: 'Add labels and notes anywhere on the plan. Tap to place, then type your text. Select text to edit font size and style.',
      },
      {
        title: 'Dimension Tool',
        content: 'Add dimension lines to show measurements. Tap start and end points - the measurement is calculated automatically.',
      },
    ],
  },
  {
    id: 'hvac-mode',
    title: 'HVAC Mode',
    icon: '❄️',
    topics: [
      {
        title: 'Entering HVAC Mode',
        content: 'Tap the HVAC button (snowflake icon) to enter HVAC design mode. This gives you access to duct drawing tools, vents, and load calculations.',
      },
      {
        title: 'Drawing Ducts',
        content: 'Select Supply (blue) or Return (orange) duct type. Choose Trunk (rectangular) or Flex (round) shape. Set the size, then draw by tapping points. Ducts auto-connect at intersections.',
      },
      {
        title: 'Placing Vents',
        content: 'Use Vent (supply register) or Grille (return) buttons. Tap on the canvas to place. Select a placed vent to edit its CFM rating and size.',
      },
      {
        title: 'Equipment',
        content: 'Place HVAC equipment like air handlers, furnaces, and condensers. Select from the equipment library and tap to place on the canvas.',
      },
      {
        title: 'Labels & CFM',
        content: 'Add CFM labels to show airflow. Add duct size labels for documentation. Toggle "Auto Labels" to show sizes on all ducts.',
      },
    ],
  },
  {
    id: 'load-calculations',
    title: 'Load Calculations',
    icon: '📊',
    topics: [
      {
        title: 'Accessing Load Calc',
        content: 'In HVAC mode, tap the Load button. This opens the load calculation panel showing heating and cooling requirements.',
      },
      {
        title: 'From Floor Plan Mode',
        content: 'Uses rooms drawn on your floor plan. Each room\'s area, walls, windows, and doors are factored into the calculation automatically.',
      },
      {
        title: 'Manual Entry Mode',
        content: 'Enter building details directly: square footage, wall construction, insulation, window type, etc. Great for quick estimates without drawing.',
      },
      {
        title: 'Climate Zone',
        content: 'Select your climate zone under Design Conditions. This sets outdoor design temperatures for heating and cooling calculations.',
      },
      {
        title: 'Understanding Results',
        content: 'Results show BTU/hr for heating and cooling, plus recommended equipment tonnage. Quick Estimate uses rules of thumb (~550 sq ft per ton). Manual J is more detailed.',
      },
      {
        title: 'Adjusting Factors',
        content: 'Fine-tune calculations with: Infiltration level (air tightness), Duct location (affects losses), Daily temperature range.',
      },
    ],
  },
  {
    id: 'exporting',
    title: 'Export & Sharing',
    icon: '📤',
    topics: [
      {
        title: 'Export to PDF',
        content: 'File > Export > PDF. Choose paper size, orientation, and scale. Add a title block with project info. The PDF includes all visible layers.',
      },
      {
        title: 'Export to Image',
        content: 'File > Export > Image. Choose PNG (with transparency) or JPG. Set resolution for print or web use.',
      },
      {
        title: 'HVAC Report',
        content: 'In HVAC mode, use Export to generate a detailed HVAC report including equipment list, duct schedule, and load calculations.',
      },
      {
        title: 'Saving Projects',
        content: 'File > Save stores your project in browser storage. Use Save As to create a copy with a new name. Projects persist until browser data is cleared.',
      },
    ],
  },
  {
    id: 'tips-tricks',
    title: 'Tips & Tricks',
    icon: '💡',
    topics: [
      {
        title: 'Snap to Grid',
        content: 'Enable grid snap for precise placement. Walls snap to common angles automatically. Hold Shift for free-angle drawing.',
      },
      {
        title: 'Undo/Redo',
        content: 'Use the Undo and Redo buttons in the toolbar, or Ctrl+Z / Ctrl+Y on desktop.',
      },
      {
        title: 'Selecting Objects',
        content: 'Tap/click to select. Selected objects show handles for moving and resizing. Tap elsewhere to deselect.',
      },
      {
        title: 'Deleting Objects',
        content: 'Select an object, then tap the Delete/Trash button, or press Delete/Backspace on keyboard.',
      },
      {
        title: 'Layers',
        content: 'Use the Layers panel to show/hide different elements. Great for viewing just walls, or just HVAC without the floor plan.',
      },
      {
        title: 'PDF Import',
        content: 'Import existing plans as PDF background. Use Scale Calibration to set the correct scale by measuring a known dimension.',
      },
    ],
  },
];

export default function HelpPanel({ onClose, onShowOnboarding, isMobile = false }) {
  const [activeSection, setActiveSection] = useState('getting-started');
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [playingVideo, setPlayingVideo] = useState(null);

  const currentSection = HELP_SECTIONS.find(s => s.id === activeSection);

  // Filter topics based on search
  const filteredSections = searchQuery
    ? HELP_SECTIONS.map(section => ({
        ...section,
        topics: section.topics.filter(
          topic =>
            topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            topic.content.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(section => section.topics.length > 0)
    : HELP_SECTIONS;

  const handleReplayTutorial = () => {
    resetOnboarding();
    onShowOnboarding?.();
    onClose();
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{
        ...styles.modal,
        width: isMobile ? '100%' : '600px',
        maxHeight: isMobile ? '95vh' : '85vh',
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={styles.headerIcon}>❓</span>
            <span style={styles.headerTitle}>Help & Guide</span>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        {/* Search */}
        <div style={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search help topics..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.clearSearch}>×</button>
          )}
        </div>

        <div style={styles.body}>
          {/* Section tabs */}
          {!searchQuery && (
            <div style={styles.tabs}>
              {/* Videos tab */}
              <button
                onClick={() => {
                  setActiveSection('videos');
                  setExpandedTopic(null);
                }}
                style={{
                  ...styles.tab,
                  ...(activeSection === 'videos' ? styles.tabActive : {}),
                }}
              >
                <span style={styles.tabIcon}>🎬</span>
                <span style={styles.tabLabel}>Videos</span>
              </button>
              {HELP_SECTIONS.map(section => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setExpandedTopic(null);
                  }}
                  style={{
                    ...styles.tab,
                    ...(activeSection === section.id ? styles.tabActive : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{section.icon}</span>
                  <span style={styles.tabLabel}>{section.title}</span>
                </button>
              ))}
            </div>
          )}

          {/* Content */}
          <div style={styles.content}>
            {searchQuery ? (
              // Search results
              filteredSections.length > 0 ? (
                filteredSections.map(section => (
                  <div key={section.id} style={styles.searchSection}>
                    <div style={styles.searchSectionTitle}>
                      <span>{section.icon}</span> {section.title}
                    </div>
                    {section.topics.map((topic, index) => (
                      <TopicCard
                        key={index}
                        topic={topic}
                        isExpanded={expandedTopic === `${section.id}-${index}`}
                        onToggle={() => setExpandedTopic(
                          expandedTopic === `${section.id}-${index}` ? null : `${section.id}-${index}`
                        )}
                        searchQuery={searchQuery}
                      />
                    ))}
                  </div>
                ))
              ) : (
                <div style={styles.noResults}>
                  <span style={styles.noResultsIcon}>🔍</span>
                  <p>No results found for "{searchQuery}"</p>
                </div>
              )
            ) : activeSection === 'videos' ? (
              // Video tutorials
              <div style={styles.videosGrid}>
                {VIDEO_TUTORIALS.map(video => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    isPlaying={playingVideo === video.id}
                    onPlay={() => setPlayingVideo(playingVideo === video.id ? null : video.id)}
                  />
                ))}
                <div style={styles.videoNote}>
                  <p>Videos coming soon! Check back for tutorial updates.</p>
                </div>
              </div>
            ) : (
              // Current section topics
              currentSection && currentSection.topics.map((topic, index) => (
                <TopicCard
                  key={index}
                  topic={topic}
                  isExpanded={expandedTopic === index}
                  onToggle={() => setExpandedTopic(expandedTopic === index ? null : index)}
                />
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={handleReplayTutorial} style={styles.tutorialBtn}>
            <span>🎓</span> Replay Tutorial
          </button>
        </div>
      </div>
    </div>
  );
}

function TopicCard({ topic, isExpanded, onToggle, searchQuery }) {
  // Highlight search terms
  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} style={styles.highlight}>{part}</mark>
      ) : part
    );
  };

  return (
    <div style={styles.topicCard}>
      <button onClick={onToggle} style={styles.topicHeader}>
        <span style={styles.topicTitle}>
          {searchQuery ? highlightText(topic.title, searchQuery) : topic.title}
        </span>
        <span style={styles.topicExpand}>{isExpanded ? '▼' : '▶'}</span>
      </button>
      {isExpanded && (
        <div style={styles.topicContent}>
          <p style={styles.topicText}>
            {searchQuery ? highlightText(topic.content, searchQuery) : topic.content}
          </p>
        </div>
      )}
    </div>
  );
}

function VideoCard({ video, isPlaying, onPlay }) {
  const isPlaceholder = video.youtubeId.startsWith('PLACEHOLDER');

  return (
    <div style={styles.videoCard}>
      <div
        style={styles.videoThumbnail}
        onClick={!isPlaceholder ? onPlay : undefined}
      >
        {isPlaying && !isPlaceholder ? (
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ borderRadius: '8px' }}
          />
        ) : (
          <>
            <div style={styles.videoPlaceholder}>
              <span style={styles.videoPlayIcon}>{isPlaceholder ? '🎬' : '▶'}</span>
            </div>
            <div style={styles.videoDuration}>{video.duration}</div>
          </>
        )}
      </div>
      <div style={styles.videoInfo}>
        <h4 style={styles.videoTitle}>{video.title}</h4>
        <p style={styles.videoDescription}>{video.description}</p>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 15000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#1a2332',
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,200,255,0.05)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    fontSize: '20px',
  },
  headerTitle: {
    color: '#00c8ff',
    fontWeight: '600',
    fontSize: '16px',
  },
  closeBtn: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px 10px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  searchWrapper: {
    padding: '12px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    position: 'relative',
  },
  searchInput: {
    width: '100%',
    padding: '10px 36px 10px 14px',
    backgroundColor: '#0d1117',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  },
  clearSearch: {
    position: 'absolute',
    right: '28px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#6080a0',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '4px',
  },
  body: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#8090a0',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  tabActive: {
    backgroundColor: 'rgba(0,200,255,0.15)',
    borderColor: 'rgba(0,200,255,0.3)',
    color: '#00c8ff',
  },
  tabIcon: {
    fontSize: '14px',
  },
  tabLabel: {
    fontWeight: '500',
  },
  content: {
    flex: 1,
    padding: '16px 20px',
    overflow: 'auto',
  },
  topicCard: {
    marginBottom: '10px',
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.05)',
    overflow: 'hidden',
  },
  topicHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  topicTitle: {
    color: '#d0d8e0',
    fontSize: '14px',
    fontWeight: '500',
  },
  topicExpand: {
    color: '#6080a0',
    fontSize: '10px',
  },
  topicContent: {
    padding: '0 14px 14px',
  },
  topicText: {
    color: '#a0b0c0',
    fontSize: '13px',
    lineHeight: '1.6',
    margin: 0,
  },
  searchSection: {
    marginBottom: '20px',
  },
  searchSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#00c8ff',
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    paddingBottom: '8px',
    borderBottom: '1px solid rgba(0,200,255,0.2)',
  },
  noResults: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6080a0',
  },
  noResultsIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  highlight: {
    backgroundColor: 'rgba(255,200,0,0.3)',
    color: '#fff',
    padding: '0 2px',
    borderRadius: '2px',
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  tutorialBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px',
    backgroundColor: 'rgba(76,175,80,0.15)',
    border: '1px solid rgba(76,175,80,0.3)',
    borderRadius: '8px',
    color: '#4caf50',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Video styles
  videosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '16px',
  },
  videoCard: {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  },
  videoThumbnail: {
    position: 'relative',
    width: '100%',
    height: '135px',
    backgroundColor: 'rgba(0,0,0,0.4)',
    cursor: 'pointer',
  },
  videoPlaceholder: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, rgba(0,100,150,0.3) 0%, rgba(0,50,100,0.3) 100%)',
  },
  videoPlayIcon: {
    fontSize: '32px',
    color: 'rgba(255,255,255,0.7)',
  },
  videoDuration: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: '#fff',
    fontSize: '11px',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  videoInfo: {
    padding: '12px',
  },
  videoTitle: {
    color: '#d0d8e0',
    fontSize: '13px',
    fontWeight: '600',
    margin: '0 0 6px',
  },
  videoDescription: {
    color: '#8090a0',
    fontSize: '11px',
    lineHeight: '1.4',
    margin: 0,
  },
  videoNote: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '20px',
    color: '#6080a0',
    fontSize: '13px',
    fontStyle: 'italic',
  },
};
